import i18n from "../../i18n";import { Trans } from "react-i18next";import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./accountDetails.css";
import { resolveApiUrl } from "../../api/api";
import sessionDB from "../../utils/sessionDB";
import {
  isOnboardingFeatureUnavailableError,
  isGenericOnboardingErrorMessage,
  parseOnboardingErrorResponse,
  handleOnboardingApiError
} from "./onboardingFeatureAccess";

const ACTION_ORDER = ["add", "search", "update"];

const ACTIONS = {
  add: {
    title: "Save Account Details",
    serviceCode: "HR05A",
    endpoint: "/saas/hrms/account-details/add",
    method: "POST"
  },
  search: {
    title: "Search",
    serviceCode: "HR05S",
    endpoint: "/saas/hrms/account-details/search/{empId}",
    method: "GET"
  },
  update: {
    title: "Update",
    serviceCode: "HR05U",
    endpoint: "/saas/hrms/account-details/update/{empId}",
    method: "PUT"
  }
};

const PAYMENT_TYPES = ["BANK", "UPI", "CASH"];

const PAYMENT_TYPE_OPTION_KEYS = {
  BANK: "onboarding.options.paymentType.bank",
  UPI: "onboarding.options.paymentType.upi",
  CASH: "onboarding.options.paymentType.cash"
};

const FIELD_LABEL_KEYS = {
  empId: "common.employeeId",
  paymentType: "onboarding.labels.paymentType",
  bankName: "onboarding.labels.bankName",
  accNo: "onboarding.labels.accountNumber",
  accName: "onboarding.labels.accountHolderName",
  branchName: "onboarding.labels.branchName",
  ifscCode: "onboarding.labels.ifscCode",
  upiId: "onboarding.labels.upiId"
};

function getFieldLabel(name) {
  const key = FIELD_LABEL_KEYS[name];
  return key ? i18n.t(key) : name;
}

function getFieldLabelsMap() {
  return Object.fromEntries(
    Object.keys(FIELD_LABEL_KEYS).map((name) => [name, getFieldLabel(name)])
  );
}

function getRuleMessage(errorKey, fieldName) {
  const fullKey = `onboarding.errors.${errorKey}`;
  return i18n.exists(fullKey)
    ? i18n.t(fullKey)
    : i18n.t("validation.fieldRequired", { field: getFieldLabel(fieldName) });
}

function getRequiredFieldMessage(name) {
  return i18n.t("validation.fieldRequired", { field: getFieldLabel(name) });
}

function translatePaymentTypeLabel(value) {
  const key = PAYMENT_TYPE_OPTION_KEYS[value];
  return key ? i18n.t(key) : value;
}

const INITIAL_FORM = {
  empId: "",
  paymentType: "",
  bankName: "",
  accNo: "",
  accName: "",
  branchName: "",
  ifscCode: "",
  upiId: ""
};

const REQUIRED_BY_ACTION = {
  add: ["empId", "paymentType"],
  update: ["empId"],
  search: ["empId"]
};

const RULES = {
  empId: (value) => !value.trim() ? getRuleMessage("empIdRequired", "empId") : !/^\d+$/.test(value.trim()) ? getRuleMessage("empIdMustBeNumeric", "empId") : "",
  paymentType: (value) => !value ? "" : !PAYMENT_TYPES.includes(value) ? getRuleMessage("chooseValidPaymentType", "paymentType") : "",
  bankName: (value) => !value ? "" : value.trim().length > 100 ? i18n.t("onboarding.errors.max100Characters") : "",
  accNo: (value) => !value ? "" : !/^[A-Z0-9]{6,24}$/.test(value.trim()) ? i18n.t("onboarding.errors.accountNumberFormat") : "",
  accName: (value) => !value ? "" : value.trim().length < 2 ? i18n.t("onboarding.errors.enterFullNameAsInBank") : value.trim().length > 100 ? i18n.t("onboarding.errors.max100Characters") : "",
  branchName: (value) => !value ? "" : value.trim().length > 100 ? i18n.t("onboarding.errors.max100Characters") : "",
  ifscCode: (value) => !value ? "" : !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(value.trim()) ? i18n.t("onboarding.errors.ifscFormat") : "",
  upiId: (value) => !value ? "" : /\s/.test(value) ? i18n.t("onboarding.errors.upiNoSpaces") : value.trim().length > 100 ? i18n.t("onboarding.errors.max100Characters") : ""
};

function buildUrl(template, form) {
  return template.replace("{empId}", encodeURIComponent(form.empId.trim()));
}

function getNextAction(currentAction) {
  const currentIndex = ACTION_ORDER.indexOf(currentAction);
  return ACTION_ORDER[(currentIndex + 1) % ACTION_ORDER.length];
}

function Field({ id, label, required, error, hint, children }) {
  return (
    <div className="acd-field">
      <label className={`acd-label${required ? " required" : ""}`} htmlFor={id}>{label}</label>
      {children}
      {hint ? <p className="acd-hint">{hint}</p> : null}
      <p className="acd-error" role="alert">{error || ""}</p>
    </div>);

}

export default function AccountDetails({ accessToken, tenantCode, onBack, onNext, onSuccess, breadcrumb, initialEmployeeId = "", isTabMode = false, isActive = true, saveTriggerRef }) {
  useEffect(() => {
    if (saveTriggerRef) {
      saveTriggerRef.current = {
        submit: handleSave
      };
    }
  });
  const [action, setAction] = useState("add");
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [banner, _setBanner] = useState(null);
  const setBanner = (val) => {
    _setBanner((prev) => {
      const next = typeof val === "function" ? val(prev) : val;
      if (next && next.type === "error") {
        if (isOnboardingFeatureUnavailableError(next.msg)) {
          return { type: "error", msg: i18n.t("onboarding.featureUnavailable") };
        }
        if (isGenericOnboardingErrorMessage(next.msg)) {
          return prev;
        }
      }
      return next;
    });
  };
  const requiredFields = useMemo(() => new Set(REQUIRED_BY_ACTION[action] || []), [action]);
  const currentAction = ACTIONS[action];

  const fetchAccount = useCallback(async () => {
    if (!initialEmployeeId) return;
    try {
      const response = await fetch(resolveApiUrl(`/saas/hrms/account-details/search/${initialEmployeeId}`), {
        method: 'GET',
        headers: {
          'x-tenant-code': tenantCode || "",
          'x-tenant-id': String(sessionDB.getTenantId() || ""),
          'x-service-code': 'HR05S',
          authorization: `Bearer ${accessToken || sessionDB.getAccessToken()}`
        }
      });
      if (response.ok) {
        const body = await response.json();
        const data = body.data || body;
        if (data) {
          setForm({
            empId: String(initialEmployeeId),
            paymentType: data.paymentType || "",
            bankName: data.bankName || "",
            accNo: data.accNo || "",
            accName: data.accName || "",
            branchName: data.branchName || "",
            ifscCode: data.ifscCode || "",
            upiId: data.upiId || ""
          });
          setAction("update");
        }
      }
    } catch (err) {
      console.error("Failed to load account details info", err);
    }
  }, [initialEmployeeId, tenantCode, accessToken]);

  useEffect(() => {
    if (!isActive) return;
    if (!initialEmployeeId) {
      setForm({
        empId: "",
        paymentType: "Bank Transfer",
        bankName: "",
        accNo: "",
        accName: "",
        branchName: "",
        ifscCode: "",
        upiId: ""
      });
      setAction("add");
      return;
    }
    setForm((prev) => ({ ...prev, empId: String(initialEmployeeId) }));
    fetchAccount();
  }, [isActive, initialEmployeeId, fetchAccount]);

  function resetFeedback() {
    setBanner(null);
  }

  function validateField(name, value, touchMap = touched) {
    const requiredMessage = requiredFields.has(name) && !String(value || "").trim() ? getRequiredFieldMessage(name) : "";
    const ruleMessage = RULES[name] ? RULES[name](value || "") : "";
    const message = requiredMessage || ruleMessage;
    setErrors((prev) => ({ ...prev, [name]: touchMap[name] ? message : "" }));
    return !message;
  }

  function validateAll() {
    const nextTouched = {};
    const nextErrors = {};
    let valid = true;

    Object.keys(RULES).forEach((name) => {
      nextTouched[name] = true;
      const requiredMessage = requiredFields.has(name) && !String(form[name] || "").trim() ? getRequiredFieldMessage(name) : "";
      const ruleMessage = RULES[name] ? RULES[name](form[name] || "") : "";
      const message = requiredMessage || ruleMessage;
      nextErrors[name] = message;
      if (message) {
        valid = false;
      }
    });

    setTouched(nextTouched);
    setErrors(nextErrors);
    return valid;
  }

  function handleActionChange(nextAction) {
    setAction(nextAction);
    setErrors({});
    setTouched({});
    resetFeedback();
  }

  function handleInputChange(event) {
    const { name, value } = event.target;
    const nextValue = name === "ifscCode" ? value.toUpperCase().replace(/\s/g, "") : value;
    setForm((prev) => ({ ...prev, [name]: nextValue }));
    if (touched[name]) {
      validateField(name, nextValue);
    }
  }

  function handleInputBlur(event) {
    const { name, value } = event.target;
    const nextTouched = { ...touched, [name]: true };
    setTouched(nextTouched);
    validateField(name, value, nextTouched);
  }

  async function submitMutation() {
    if (!validateAll()) {
      return;
    }

    setLoading(true);
    resetFeedback();

    try {
      const payload = {
        empId: Number(form.empId.trim()),
        paymentType: form.paymentType || undefined,
        bankName: form.bankName.trim() || undefined,
        accNo: form.accNo.trim() || undefined,
        accName: form.accName.trim() || undefined,
        branchName: form.branchName.trim() || undefined,
        ifscCode: form.ifscCode.trim() || undefined,
        upiId: form.upiId.trim() || undefined
      };

      Object.keys(payload).forEach((key) => {
        if (payload[key] === undefined || payload[key] === "") {
          delete payload[key];
        }
      });

      const response = await fetch(resolveApiUrl(buildUrl(currentAction.endpoint, form)), {
        method: currentAction.method,
        headers: {
          "Content-Type": "application/json",
          "x-tenant-code": tenantCode || "",
          "x-service-code": currentAction.serviceCode,
          authorization: `Bearer ${accessToken}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const { errorMessage, errorBody } = await parseOnboardingErrorResponse(response, getFieldLabelsMap());
        handleOnboardingApiError(errorBody, errorMessage, { setErrors, setTouched, setBanner, fieldLabels: getFieldLabelsMap() });
        return;
      }

      const data = await response.json().catch(() => ({ message: i18n.t("onboarding.messages.saveAccountDetailsCompleted") }));
      setBanner({
        type: "success",
        msg: data.message || (action === "add"
          ? i18n.t("onboarding.messages.saveAccountDetailsCompleted")
          : i18n.t("onboarding.messages.actionCompletedSuccessfully", { action: i18n.t("common.update") }))
      });
      if (action === "update") {
        await fetchAccount();
      }
      if (onSuccess) {
        onSuccess(data);
      }
    } catch (error) {
      handleOnboardingApiError(null, error.message, { setErrors, setTouched, setBanner, fieldLabels: getFieldLabelsMap() });
    } finally {
      setLoading(false);
    }
  }

  async function runSearch() {
    if (!validateAll()) {
      return;
    }

    setLoading(true);
    resetFeedback();

    try {
      const response = await fetch(resolveApiUrl(buildUrl(currentAction.endpoint, form)), {
        method: currentAction.method,
        headers: {
          "Content-Type": "application/json",
          "x-tenant-code": tenantCode || "",
          "x-service-code": currentAction.serviceCode,
          authorization: `Bearer ${accessToken}`
        }
      });

      if (!response.ok) {
        const { errorMessage, errorBody } = await parseOnboardingErrorResponse(response, getFieldLabelsMap());
        handleOnboardingApiError(errorBody, errorMessage, { setErrors, setTouched, setBanner, fieldLabels: getFieldLabelsMap() });
        return;
      }

      await response.json();
      setBanner({ type: "success", msg: i18n.t("onboarding.messages.accountDetailsLoaded") });
    } catch (error) {
      handleOnboardingApiError(null, error.message, { setErrors, setTouched, setBanner, fieldLabels: getFieldLabelsMap() });
    } finally {
      setLoading(false);
    }
  }

  function handleSave() {
    if (action === "search") {
      runSearch();
      return;
    }
    submitMutation();
  }

  function handleNext() {
    const nextAction = getNextAction(action);
    handleActionChange(nextAction);
    if (onNext) {
      onNext(nextAction);
    }
  }

  return (
    <div className={`acd-page${isTabMode ? " is-tab-mode" : ""}`}>
      <main className="acd-main">
        {!isTabMode && breadcrumb &&
        <div className="acd-breadcrumb" aria-label={i18n.t("common.breadcrumb")}>
            {breadcrumb.map((item, index) =>
          <React.Fragment key={item.label}>
                {index > 0 && <span className="acd-bc-sep">/</span>}
                <button className={`acd-bc-item${item.active ? " active" : ""}`} onClick={item.active ? undefined : item.onClick} disabled={item.active}>
                  {item.label}
                </button>
              </React.Fragment>
          )}
          </div>
        }

        {!isTabMode &&
        <div className="acd-page-header">
            <div className="acd-page-header-row">
              <div>
                <h1 className="acd-page-title"><Trans i18nKey="onboarding.accountDetails" /></h1>
                <p className="acd-page-sub"><Trans i18nKey="onboarding.manageEmployeePaymentAndBankAccountDetails" /></p>
              </div>
              <button className="acd-header-action" type="button" onClick={() => handleActionChange(action === "update" ? "add" : "update")}>
                {action === "update" ? i18n.t("onboarding.addNew") : i18n.t("common.update")}
              </button>
            </div>
          </div>
        }

        <div className="acd-form-card">
          <div className="acd-section">
            <p className="acd-section-title"><Trans i18nKey="onboarding.paymentDetails" /></p>
            <div className="acd-grid">
              <Field id="empId" label={getFieldLabel("empId")} required error={touched.empId && errors.empId}>
                <input id="empId" name="empId" value={form.empId} onChange={handleInputChange} onBlur={handleInputBlur} className={`acd-input${touched.empId && errors.empId ? " error" : ""}`} placeholder={i18n.t("onboarding.enterEmployeeId")} readOnly={Boolean(initialEmployeeId)} />
              </Field>
              <Field id="paymentType" label={getFieldLabel("paymentType")} required error={touched.paymentType && errors.paymentType}>
                <div className="acd-select-wrap">
                  <select id="paymentType" name="paymentType" value={form.paymentType} onChange={handleInputChange} onBlur={handleInputBlur} className={`acd-select${touched.paymentType && errors.paymentType ? " error" : ""}`}>
                    <option value=""><Trans i18nKey="onboarding.selectPaymentType" /></option>
                    {PAYMENT_TYPES.map((option) => <option key={option} value={option}>{translatePaymentTypeLabel(option)}</option>)}
                  </select>
                </div>
              </Field>
              <Field id="bankName" label={getFieldLabel("bankName")} error={touched.bankName && errors.bankName}>
                <input id="bankName" name="bankName" value={form.bankName} onChange={handleInputChange} onBlur={handleInputBlur} className={`acd-input${touched.bankName && errors.bankName ? " error" : ""}`} placeholder={i18n.t("onboarding.stateBankOfIndia")} />
              </Field>
              <Field id="accNo" label={getFieldLabel("accNo")} error={touched.accNo && errors.accNo}>
                <input id="accNo" name="accNo" value={form.accNo} onChange={handleInputChange} onBlur={handleInputBlur} className={`acd-input${touched.accNo && errors.accNo ? " error" : ""}`} placeholder={i18n.t("onboarding.xxxxxxxxxxxxxxxxxx")} />
              </Field>
              <Field id="accName" label={getFieldLabel("accName")} error={touched.accName && errors.accName}>
                <input id="accName" name="accName" value={form.accName} onChange={handleInputChange} onBlur={handleInputBlur} className={`acd-input${touched.accName && errors.accName ? " error" : ""}`} placeholder={i18n.t("onboarding.fullNameAsInBank")} />
              </Field>
              <Field id="branchName" label={getFieldLabel("branchName")} error={touched.branchName && errors.branchName}>
                <input id="branchName" name="branchName" value={form.branchName} onChange={handleInputChange} onBlur={handleInputBlur} className={`acd-input${touched.branchName && errors.branchName ? " error" : ""}`} placeholder={i18n.t("onboarding.mainBranch")} />
              </Field>
              <Field id="ifscCode" label={getFieldLabel("ifscCode")} error={touched.ifscCode && errors.ifscCode}>
                <input id="ifscCode" name="ifscCode" value={form.ifscCode} onChange={handleInputChange} onBlur={handleInputBlur} className={`acd-input${touched.ifscCode && errors.ifscCode ? " error" : ""}`} placeholder={i18n.t("onboarding.sbin0001234")} />
              </Field>
              <Field id="upiId" label={getFieldLabel("upiId")} error={touched.upiId && errors.upiId}>
                <input id="upiId" name="upiId" value={form.upiId} onChange={handleInputChange} onBlur={handleInputBlur} className={`acd-input${touched.upiId && errors.upiId ? " error" : ""}`} placeholder={i18n.t("onboarding.nameUpi")} />
              </Field>
            </div>
          </div>

          <div className="acd-section">
            {banner && <div className={`acd-banner ${banner.type}`}>{banner.msg}</div>}
            {!isTabMode &&
            <div className="acd-actions">
                <button className="acd-secondary-btn" type="button" onClick={onBack}><Trans i18nKey="formActions.back" /></button>
                <button className="acd-primary-btn" type="button" onClick={handleSave} disabled={loading}>
                  {loading ? i18n.t("localConveyance.saving") : i18n.t("formActions.save")}
                </button>
                <button className="acd-secondary-btn" type="button" onClick={handleNext}><Trans i18nKey="pagination.next2" /></button>
              </div>
            }
          </div>
        </div>

      </main>
    </div>);

}
